/*
 * Copyright (c) 2018-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import React, {useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {useOktaAuth} from '@okta/okta-react';
import {useHistory} from 'react-router-dom';
import OktaSignIn from '@okta/okta-signin-widget';
import {setIsUserLoggedIn} from './redux/actions/uiActions';
import '@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
//import logo from './logo.svg';

import {getCurrentRegionOIDCConfigs} from './components/utils/APIsUtils/httpsUtils';
const currentRegionOIDCConfigs = getCurrentRegionOIDCConfigs();

const Login = ({setCorsErrorModalOpen, setIsUserLoggedIn}) => {
  const {oktaAuth} = useOktaAuth();
  const widgetRef = useRef();
  const router = useHistory();

  // Fetch otp and state from query params from email callback verification URI
  // Application should have http://localhost:8080/login as the email callback verification URI
  const queryParams = new URLSearchParams(window.location.search);
  const otp = queryParams.get('otp');
  const state = queryParams.get('state');

  useEffect(() => {
    setIsUserLoggedIn(false);
    const oktaToken = JSON.parse(localStorage.getItem('okta-token-storage'));
    if (oktaToken) {
      const {idToken = false} = oktaToken;
      if (idToken) {
        router.push('/Dashboard');
      }
    }
  }, []);

  useEffect(() => {
    if (!widgetRef.current) {
      return false;
    }
    const {issuer, clientId, redirectUri, scopes, useInteractionCode} = currentRegionOIDCConfigs;
    const isLocalhost =
      window &&
      window.location &&
      window.location.href &&
      window.location.href.indexOf &&
      window.location.href.indexOf('localhost') !== -1
        ? true
        : false;
    const widget = new OktaSignIn({
      /**
       * Note: when using the Sign-In Widget for an OIDC flow, it still
       * needs to be configured with the base URL for your Okta Org. Here
       * we derive it from the given issuer for convenience.
       */
      baseUrl: issuer.split('/oauth2')[0],
      clientId,
      redirectUri: isLocalhost ? 'http://localhost:3000/dashboard' : redirectUri,
      logo: 'https://ok1static.oktacdn.com/fs/bco/1/fs0yd3dsz4BFQJUFXCSD',
      i18n: {
        en: {
          'primaryauth.title': 'Sign in to Amway ABO Academy',
        },
      },
      authParams: {
        // To avoid redirect do not set "pkce" or "display" here. OKTA-335945
        issuer,
        scopes,
      },
      useInteractionCodeFlow: useInteractionCode, // Set to true, if your org is OIE enabled
      state,
      otp,
    });

    widget.renderEl(
      {el: widgetRef.current},
      (res) => {
        oktaAuth.handleLoginRedirect(res.tokens);
      },
      (err) => {
        throw err;
      },
    );

    // Note: Can't distinguish CORS error from other network errors
    const isCorsError = (err) => err.name === 'AuthApiError' && !err.statusCode;

    widget.on('afterError', (_context, error) => {
      if (isCorsError(error)) {
        setCorsErrorModalOpen(true);
      }
    });

    return () => widget.remove();
  }, [oktaAuth]);

  return (
    <div
      style={{
        margin: '0 auto',
        paddingTop: '5%',
      }}
    >
      <div ref={widgetRef} />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => ({
  setIsUserLoggedIn: (options) => dispatch(setIsUserLoggedIn(options)),
});

export default React.memo(connect(null, mapDispatchToProps)(Login));
